<template>
    <div
        v-if="banner"
        id="banner-a"
        class="relative h-svh w-full overflow-hidden"
        :data-theme="theme"
        :data-mode="borderMode"
        :class="{
            'surface-primary p-3': banner?.bannerA_ShowBorder,
        }"
        :style="bannerHeight"
    >
        <!-- carousel or video -->
        <component
            :is="backgroundTypeComponent"
            :data-mode="mode"
            :banner="banner"
            :class="{
                'rounded-card-lg h-full overflow-hidden': banner?.bannerA_ShowBorder,
            }"
        />

        <div
            :data-mode="mode"
            class="absolute inset-0 z-10 px-3 py-32"
            :class="{
                'px-6': banner?.bannerA_ShowBorder,
            }"
        >
            <div class="container-extra-wide relative mx-auto h-full">
                <div
                    class="flex h-full w-full px-4 md:px-0"
                    :class="{
                        'items-center': ['left', 'center', 'right'].includes(banner?.bannerA_Alignment),
                        'justify-end': ['top-right', 'right', 'bottom-right'].includes(banner?.bannerA_Alignment),
                        'justify-center': ['top', 'center', 'bottom'].includes(banner?.bannerA_Alignment),
                        'items-end': ['bottom-left', 'bottom', 'bottom-right'].includes(banner?.bannerA_Alignment),
                    }"
                >
                    <div
                        class="flex flex-col"
                        :class="{
                            'max-w-[576px]': banner?.bannerA_ContentContainerWidth === 'narrow',
                            'max-w-[768px]': banner?.bannerA_ContentContainerWidth === 'wide',
                            'items-center text-center': ['center', 'top', 'bottom'].includes(banner?.bannerA_Alignment),
                            'items-end text-right': ['right', 'top-right', 'bottom-right'].includes(
                                banner?.bannerA_Alignment,
                            ),
                        }"
                    >
                        <!-- Logo -->
                        <div
                            v-if="headerLogo[0]?.url?.length"
                            id="banner-a-logo"
                            class="mb-8 cursor-pointer"
                        >
                            <NuxtLink
                                :to="homeUri"
                                no-prefetch
                                aria-label="Home"
                            >
                                <img
                                    :src="headerLogo[0]?.url"
                                    :alt="`${variables?.siteName} logo`"
                                    :class="logoSize"
                                >
                            </NuxtLink>
                        </div>
                        <RichText
                            :text="banner?.bannerA_Content"
                            :heading-size="banner?.bannerA_HeadingSize"
                            :body-text-size="banner?.bannerA_BodyTextSize"
                            class="mb-6"
                        />

                        <div
                            v-if="banner?.bannerA_Buttons && banner?.bannerA_Buttons?.length"
                            class="flex w-fit flex-row flex-wrap gap-2"
                            :class="{
                                'justify-start': ['left', 'top-left', 'bottom-left'].includes(
                                    banner?.bannerA_Alignment,
                                ),
                                'justify-center': ['center', 'top', 'bottom'].includes(banner?.bannerA_Alignment),
                                'justify-end': ['right', 'top-right', 'bottom-right'].includes(
                                    banner?.bannerA_Alignment,
                                ),
                            }"
                        >
                            <NuxtLink
                                v-for="(button, buttonIndex) in banner?.bannerA_Buttons"
                                :key="buttonIndex"
                                :to="button.url"
                            >
                                <DButton
                                    size="lg"
                                    :theme="buttonTheme(button.buttonStyle)"
                                    :right-icon="iconName(button?.icon)"
                                >
                                    {{ button.text }}
                                </DButton>
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <slot name="bannerDecoration" />
    </div>
</template>

<script setup>
    import { DButton } from '@digistorm/spark'

    const { iconName } = useIcons()

    const { homeUri } = storeToRefs(useGlobalStore())
    const { variables } = storeToRefs(useEntryStore())
    const { buttonTheme } = useButtonTheme()
    const { bannerHasBorder } = storeToRefs(useGlobalStore())
    const { entry } = storeToRefs(useEntryStore())
    const navHeight = ref(0)

    const props = defineProps({
        navOverlayBanner: Boolean,
    })

    const banner = computed(() => {
        return first(entry.value?.banner?.filter((b) => b.__typename === 'ds_BannerA_Entry'))
    })

    const backgroundType = computed(() => {
        const bannerVideos = get(banner.value, 'bannerA_Video', null)
        return bannerVideos?.length > 0 ? 'video' : 'carousel'
    })

    const backgroundTypeComponents = import.meta.glob('./*.async.vue')

    const backgroundTypeComponent = defineAsyncComponent(() => {
        const background = backgroundTypeComponents[`./${backgroundType.value}.async.vue`]
        return background ? background() : Promise.reject(new Error(`Unknown background type: ${backgroundType.value}`))
    })

    const theme = computed(() => {
        return get(banner.value, 'bannerA_ColorTheme')
    })

    const mode = computed(() => {
        switch (get(banner.value, 'bannerA_ColorPalette')) {
            case 'color-subtle':
                return 'brand'
            case 'color-invert':
                return 'brand-invert'
            default:
                return get(banner.value, 'bannerA_ColorPalette') || 'brand-invert'
        }
    })

    const borderMode = computed(() => {
        return get(banner.value, 'bannerA_BorderColorPalette')
    })

    function updateNavHeight() {
        const navWrapper = document.querySelector('[data-nav-wrapper] > div')
        if (navWrapper) {
            navHeight.value = navWrapper.clientHeight
        }
    }

    const bannerHeight = computed(() => {
        if (props.navOverlayBanner) {
            return
        }
        return {
            height: `calc(100vh - ${navHeight.value}px)`,
        }
    })

    const logoScale = (size) => {
        if (['top', 'center', 'bottom'].includes(get(banner.value, 'bannerA_Alignment'))) {
            switch (size) {
                case 'sm':
                    return 'scale-50 translate-y-[25%]'
                case 'lg':
                    return 'scale-150 translate-y-[-25%]'
                case 'md':
                default:
                    return 'max-lg:scale-60 max-lg:translate-y-[20%]'
            }
        } else if (['right', 'top-right', 'bottom-right'].includes(get(banner.value, 'bannerA_Alignment'))) {
            switch (size) {
                case 'sm':
                    return 'scale-50 translate-x-[25%] translate-y-[25%]'
                case 'lg':
                    return 'scale-150 translate-x-[-25%] translate-y-[-25%]'
                case 'md':
                default:
                    return 'max-lg:scale-60 max-lg:translate-x-[20%] max-lg:translate-y-[20%]'
            }
        } else {
            switch (size) {
                case 'sm':
                    return 'scale-50 translate-x-[-25%] translate-y-[25%]'
                case 'lg':
                    return 'scale-150 translate-x-[25%] translate-y-[-25%]'
                case 'md':
                default:
                    return 'max-lg:scale-60 max-lg:translate-x-[-20%] max-lg:translate-y-[20%]'
            }
        }
    }

    const logoSize = computed(() => {
        return logoScale(get(banner.value, 'bannerA_LogoSize'))
    })

    const headerLogo = computed(() => {
        return get(banner.value, 'bannerA_Logo')
    })

    onMounted(() => {
        // store border value to be used in nav components
        bannerHasBorder.value = get(banner.value, 'bannerA_ShowBorder', false)

        updateNavHeight()
        window.addEventListener('resize', updateNavHeight)
    })
</script>

<style>
    @reference 'tailwindcss';
    @reference '@digistorm/spark/dist/variables.css';

    #banner-a .text-heading,
    #banner-a .text-subheading,
    #banner-a .text-lead-text {
        @apply mb-4;
    }
    #banner-a .text-lead-text {
        @apply text-neutral-strong mt-0;
    }
    #banner-a .text-lead-text strong {
        @apply text-neutral-strong mt-0;
    }
    #banner-a .banner-accent {
        @apply text-subheading;
    }
    #banner-a .text-subheading {
        @apply text-neutral-strong;
    }
    #banner-a .text-subheading strong {
        @apply text-neutral-strong;
    }
    #banner-a .text-subheading .banner-accent {
        @apply text-subheading;
    }
    #banner-a .text-subheading .banner-accent strong {
        @apply text-subheading;
    }
    #banner-a .prose *:only-child {
        @apply mb-0!;
    }
</style>
